import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { ExportTemplateService } from "@/gen/omnios/v1/export_template_connect";
import type { ExportTemplate } from "@/gen/omnios/v1/export_template_pb";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import {
	Outlet,
	createFileRoute,
	useMatches,
	useNavigate,
} from "@tanstack/react-router";
import {
	FileSpreadsheet,
	Loader2,
	Plus,
	Search,
	Star,
	Trash2,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/export-templates")({
	component: ExportTemplatesLayout,
});

function ExportTemplatesLayout() {
	const matches = useMatches();
	// If a child route is active (e.g. /$id), render only the Outlet
	const hasChild = matches.some(
		(m) => m.routeId === "/settings/export-templates/$id",
	);

	if (hasChild) {
		return <Outlet />;
	}

	return <ExportTemplatesList />;
}

const client = createClient(ExportTemplateService, transport);

function ExportTemplatesList() {
	const { t } = useTranslation("settings");
	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("export_templates_breadcrumb") },
	]);

	const navigate = useNavigate();
	const [templates, setTemplates] = useState<ExportTemplate[]>([]);
	const [loading, setLoading] = useState(true);
	const [deleteId, setDeleteId] = useState<string | null>(null);
	const [search, setSearch] = useState("");

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const res = await client.listExportTemplates({
				scope: "workspace",
				page: { pageSize: 100, pageToken: "" },
			});
			setTemplates(res.templates);
		} catch {
			/* toast handled by interceptor */
		}
		setLoading(false);
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	async function doDelete() {
		if (!deleteId) return;
		try {
			await client.deleteExportTemplate({ id: deleteId });
			setDeleteId(null);
			load();
		} catch {
			/* handled */
		}
	}

	const filtered = templates.filter(
		(tpl) =>
			!search ||
			tpl.name.toLowerCase().includes(search.toLowerCase()) ||
			tpl.description.toLowerCase().includes(search.toLowerCase()),
	);

	return (
		<div className="space-y-6 w-full">
			<div className="flex items-center justify-between gap-4">
				<div className="relative">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("export_templates_search_placeholder")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9 h-9 w-56"
					/>
				</div>
				<Button
					size="sm"
					onClick={() =>
						navigate({
							to: "/settings/export-templates/$id",
							params: { id: "new" },
						})
					}
				>
					<Plus className="h-4 w-4 mr-1" />
					{t("export_templates_create")}
				</Button>
			</div>

			{/* Loading */}
			{loading ? (
				<div className="flex justify-center py-12">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			) : filtered.length === 0 ? (
				<Card>
					<CardContent className="py-12 text-center text-sm text-muted-foreground">
						<FileSpreadsheet className="mx-auto h-10 w-10 mb-3 opacity-40" />
						{t("export_templates_empty")}
					</CardContent>
				</Card>
			) : (
				<div className="rounded-lg border overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="bg-muted/30">
								<TableHead className="w-10" />
								<TableHead>{t("export_templates_col_name")}</TableHead>
								<TableHead className="w-24">
									{t("export_templates_col_format")}
								</TableHead>
								<TableHead>{t("export_templates_col_description")}</TableHead>
								<TableHead className="w-10" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{filtered.map((tpl) => (
								<TableRow
									key={tpl.id}
									className="cursor-pointer hover:bg-muted/50"
									onClick={() =>
										navigate({
											to: "/settings/export-templates/$id",
											params: { id: tpl.id },
										})
									}
								>
									<TableCell className="px-3">
										<FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
									</TableCell>
									<TableCell>
										<div className="flex items-center gap-2">
											<span className="font-medium">{tpl.name}</span>
											{tpl.isDefault && (
												<Badge variant="secondary" className="text-xs">
													<Star className="h-3 w-3 mr-0.5" />
													{t("export_templates_default")}
												</Badge>
											)}
										</div>
									</TableCell>
									<TableCell>
										<Badge variant="outline" className="text-xs uppercase">
											{tpl.format || "xlsx"}
										</Badge>
									</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{tpl.description || "—"}
									</TableCell>
									<TableCell className="px-3">
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7 text-muted-foreground hover:text-destructive"
											onClick={(e) => {
												e.stopPropagation();
												setDeleteId(tpl.id);
											}}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			{/* Delete confirmation */}
			<AlertDialog
				open={!!deleteId}
				onOpenChange={(open) => !open && setDeleteId(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("export_templates_delete_title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("export_templates_delete_desc")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction onClick={doDelete}>
							{t("common:delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
